// chat.tsx
import { useState, useContext, createRef, useEffect, Key } from 'react';

import { ChatUserContext } from '../../core/chatContext';
import { ChatController } from './chat.controller';
import { getAPIResponse } from '../../core/http';

import { DialogComponent } from '../common/dialog';
import { FileContextComponent } from './fileContext/fileContext';
import toast from 'react-hot-toast';
import styles from './chat.module.scss';

import Send from '@mui/icons-material/Send';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import StopCircleOutlinedIcon from '@mui/icons-material/StopCircleOutlined';
import { Box, Button, Modal, Tooltip, useMediaQuery } from '@mui/material';
import { MenuContext } from '../../context/menuContext';
import { TextareaAutosize } from '@mui/base/TextareaAutosize';
import { fetchWithToken } from '../../core/interceptor';
import CancelIcon from '@mui/icons-material/Cancel';

const ChatComponent: React.FC<{ idUser: any, userPhoto: any }> = ({ idUser, userPhoto }) => {
  
  const Chat_Controller = new ChatController();
  
  const currChatContext: any = useContext(ChatUserContext);
  
  const textAreaRef = createRef<HTMLTextAreaElement>();
  
  // const [fileName, setFileName] = useState<string[]>(['']);
  const [streamingActive, setStreamingActive] = useState(false);
  const [abortController, setAbortController] = useState<AbortController | null>(null);
  const { mail } = useContext(MenuContext);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);
  const [open, setOpen] = useState(false);
  const maxWidth = useMediaQuery('(max-width:480px)');
  const maxHeight = useMediaQuery('(max-height:376px)');
  const modalContainer = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: maxHeight || maxWidth ? 250 : 400,
    bgcolor: 'white',
    borderRadius: 4,
    boxShadow: 24,
    p: 3,
    opacity: 1,
  }
  
  useEffect(() => {
    // Focus in textarea when render page
    textAreaRef.current?.focus();
  });
  
  const handleStopStreaming = () => {
    if (abortController) {
      abortController.abort();
      setStreamingActive(false);
    }
  };
  
  const deleteContextDocuments = () => { 
    currChatContext.setIsUploading(false)
    currChatContext.setCurrChat((prevCurrChat: any) => {
      prevCurrChat.messages = [];
      prevCurrChat['data_sources'][0]['parameters']['new_document'] = false;
      prevCurrChat['data_sources'][0]['parameters']['conversationId'] = crypto.randomUUID();
      prevCurrChat.data_sources[0].parameters.vector_store_id = null;
      prevCurrChat.data_sources[0].parameters.thread_id = null;
      return prevCurrChat;
    });

    currChatContext.setUserAsks([]);
    currChatContext.setIaAnswers([]);
    currChatContext.setFilesSelected([]);
  }

  const PromptComponent = () => {
    const [formData, setFormData] = useState({ question: "" });
    const [textoHolder, setTextoHolder] = useState(window.innerWidth > 740 ? "Escribir mensaje..." : "Escribir...");
    

    useEffect(() => {
      setTextoHolder(window.innerWidth > 1024 ? "Escribir mensaje..." : "Escribir...");
    }, []);

    useEffect(() => {
      if (currChatContext.filesSelected.length === 0) {
        currChatContext.currChat.data_sources[0].parameters.ephemeralId = null;
      }
    }, []);


    const handleTextareaKeyPress = (e: any) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSubmit(e);
      }
    };

    const handleSubmit = (e: any) => {
      e.preventDefault();
      if (formData.question) {
        if (currChatContext.isLoading || currChatContext.isUploading) {
          toast.error("Espere a que termine el proceso en curso.", { duration: 2000 });
          return; // 2 seconds
        }
        // Add new question to array
        currChatContext['setCurrChat']((prevContext: RequestOpenAI) => {
          prevContext.messages = [...prevContext.messages || [], {
            "role": "user",
            "content": formData.question,
            "evaluation": "",
            "comment": ""
          }]
          return prevContext
        });

        // Update user question
        currChatContext.setUserAsks((prevArray: any) => [...prevArray, formData.question]);

        // Add loading message
        currChatContext.setIaAnswers((prevArray: DialogComponents[]) => [...prevArray, { id: '0', content: 'Generando respuesta...' }]);
        currChatContext.setIsLoading(true);

        const newAbortController = new AbortController();
        setAbortController(newAbortController);
        setStreamingActive(true);
        let apiResponse = '';

        getAPIResponse(currChatContext, idUser, mail, (chunk) => {
          if (!newAbortController.signal.aborted) {
            chunk.forEach((part: any) => {
              apiResponse += part.delta.content;
              currChatContext.setIaAnswers((prevArray: DialogComponents[]) => [...prevArray.slice(0, prevArray.length - 1), { id: '0', content: apiResponse }]);
            });

          }
        }, newAbortController)
          .then(() => {
            Chat_Controller.saveResponse(currChatContext, idUser, formData.question, apiResponse, mail);
          })
          .catch((error) => {
            console.info('Error', error);
          })
          .finally(() => {
            currChatContext.setIsLoading(false);
            setStreamingActive(false);
          })
      } else {
        toast.error("Debe escribir un mensaje.", { duration: 2000 }); // 2 seconds 
      }
    };

    const handleFileChange = async (event: any) => {
      // currChatContext.setIsLoading(true);
      currChatContext.setIsUploading(true);

      const _formData = new FormData();
      // currChatContext.setFilesSelected([])

      const _files = event.target.files

      for (const [, value] of Object.entries(_files)) {
        const file = value as File;

        if (!Chat_Controller.checkFileSize(file.size)) {
          currChatContext.setIsUploading(false);
          return toast.error('El fichero supera el tamaño máximo permitido');
        }

        if (!Chat_Controller.checkFilesTypes(file)) {
          currChatContext.setIsUploading(false);
          return toast.error('El fichero no es del tipo correcto');
        }

        const isDuplicate = currChatContext.filesSelected.some((prevFile: File) => 
          prevFile.name === file.name && prevFile.size === file.size && prevFile.lastModified === file.lastModified
        );

        if (isDuplicate) {
          currChatContext.setIsUploading(false);
          return toast.error('Está intentando subir archivos que ya están añadidos.');
        }

        _formData.append("files", file);
      }

      await currChatContext.setFilesSelected((prevArray: File[]) => {
        prevArray.forEach((prevFile) => {
          _formData.append("files", prevFile);
        });
        return [..._formData.getAll('files')];
      });

      let url: string = '';

      if (!currChatContext.currChat.data_sources[0].parameters.vector_store_id){
        const responseVector: any = await fetchWithToken(`/v1/comparador/create`).then((response) => response.json());
        const responseThread: any = await fetchWithToken(`/v1/comparador/create_thread`).then((response) => response.json());

        if (responseVector.vector_store_id && responseThread.thread_id) {
          await currChatContext.setCurrChat((prevCurrChat: any) => {
            prevCurrChat.messages = [...prevCurrChat.messages || []];
            prevCurrChat['data_sources'][0]['parameters']['vector_store_id'] = responseVector.vector_store_id;
            prevCurrChat['data_sources'][0]['parameters']['thread_id'] = responseThread.thread_id;
            return prevCurrChat;
          });
        }
        url = `/v1/comparador/upload/${responseVector.vector_store_id}`;
        Chat_Controller.uploadFilesToComparator(_formData, url)
          .then(() => {
            currChatContext.setIsUploading(false);
            currChatContext.setUserAsks([]);
            currChatContext.setIaAnswers([]);
            // Remove conversation
            currChatContext.setCurrChat((prevCurrChat: any) => {
              // Remove chat history
              prevCurrChat.messages = [];
              // Indicate start new document
              prevCurrChat['data_sources'][0]['parameters']['new_document'] = true;
              // Indicate new ephemaral document id
              // prevCurrChat['data_sources'][0].parameters.ephemeralId = currChatContext.currChat.data_sources[0].parameters.ephemeralId;
              return prevCurrChat;
            });
          }).catch((error) => {
            currChatContext.setIsUploading(false);
            currChatContext.setFilesSelected([])
          });
      }else{
        url = `/v1/comparador/upload/${currChatContext.currChat.data_sources[0].parameters.vector_store_id}`;

        const _formDataAdd = new FormData();

        for (const [, value] of Object.entries(_files)) {
          const file = value as File;
          _formDataAdd.append("files", file);
        }
        Chat_Controller.uploadFilesToComparator(_formDataAdd, url)
          .then(() => {
            currChatContext.setIsUploading(false);
          }).catch((error) => {
            currChatContext.setIsUploading(false);
            currChatContext.setFilesSelected([])
          });

      }
    };

    return (
      <div className={styles.promptComponentContainer}>
        <div className={styles.promptComponentComponent}>
          {/* <ParametersComponent /> */}
          <form id="a-form" className={styles.promptComponentComponentInput} onSubmit={handleSubmit}>
            <label onClick={() => {
              if (currChatContext.isUploading) toast.error("Espere a que finalice el proceso en curso.", { duration: 2000 });
            }}
              htmlFor="file-upload" style={{ cursor: 'pointer' }}>
              <div className={styles.promptComponentComponentUploadButton}>
                <Tooltip placement="top" arrow title="Adjuntar archivos">
                  <AttachFileIcon className={styles.promptComponentComponentIcon}></AttachFileIcon>
                </Tooltip>
              </div>
            </label>
            <input
              disabled={currChatContext.isUploading}
              id="file-upload"
              type="file"
              multiple={true}
              style={{ display: 'none' }}
              onChange={handleFileChange}
              accept='.pdf, .doc, .docx, .txt'
            />
            <div className={styles.promptComponentComponentInputTextareaContainer}>
              <TextareaAutosize
                minRows={1}
                maxRows={8}
                ref={textAreaRef}
                className={styles.promptComponentComponentInputTextarea}
                onChange={(e) => {
                  setFormData({ ...formData, question: e.target.value })
                  // e.target.style.height = 'auto'
                  // e.target.style.height = `${e.target.scrollHeight}px`
                }}
                onKeyDown={handleTextareaKeyPress} // Handles the "Enter" key
                value={formData.question}
                id="body"
                name="body"
                placeholder={textoHolder}
                style={{ overflowY: 'auto' }}></TextareaAutosize>
            </div>


          </form>
          {
            !streamingActive && (
              <button form="a-form" className={styles.promptComponentComponentSendButton} type='submit'>
                <Send className={styles.promptComponentComponentIcon}></Send>
                <span>Enviar</span>
              </button>
            )
          }
        </div>
      </div>
    )
  }

  const showMainComponent = () => {
    return (
      <DialogComponent
        userId={idUser}
        userPhoto={userPhoto}
        userAsks={currChatContext.userAsks}
        iaAnswers={currChatContext.iaAnswers}
        enabledFeedBack={true}
      ></DialogComponent>
    )
  }


  return (
    <div className={styles.chatComponent}>
      {showMainComponent()}

      {currChatContext.isUploading 
      ? 
        <div className={styles.spinnerContainer}>
          <div className={styles.spinner}></div>
          <div className={styles.message}>Actualizando índice de documentos ...</div>
        </div> 
      : 
      currChatContext.filesSelected.length > 0 &&
        <div className={styles.contentImageList}>
            <Tooltip placement="top" arrow title="Borrar lista de documentos">
              <div className={styles.contentRemoveList} onClick={() => {
                handleOpen();
              }}><CancelIcon /></div>
            </Tooltip>          
          {currChatContext.filesSelected.map((file: any, index: Key | null | undefined) => {
            return (
              <FileContextComponent key={index} fileName={file.name} setFileName={currChatContext.setFilesSelected} currChatContext={currChatContext} />
            )
          })}
        </div>
      }

      {/* <div className={styles.separator}></div> */}
      <div className={styles.promptContainer}>
        <PromptComponent />
        {streamingActive && (
          <button type="button" onClick={() => {
            if (!currChatContext.isLoading) {
              return;
            } else { toast.error("Espere a que termine le proceso en curso.", { duration: 2000 }); }
          }} className={styles.promptComponentComponentCloseButton}>
            <StopCircleOutlinedIcon className={styles.promptComponentComponentIcon}></StopCircleOutlinedIcon>
          </button>
        )}
      </div>
      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box sx={modalContainer}>
          <h4 className={styles.titleRemoveList}>Se perderá la indexación de documentos y se iniciará una nueva conversación.</h4>
          <div className={styles.modalContainerButtons}>
            <Button sx={{ '&:hover': { backgroundColor: "#003cff", color: "#e1f1ff" }, color: "#2e2e2e" }} onClick={() => {
              handleClose();
              deleteContextDocuments();
            }}>Aceptar</Button>
            <Button sx={{ '&:hover': { backgroundColor: "#003cff", color: "#e1f1ff" }, color: "#2e2e2e" }} onClick={handleClose}>Cancelar</Button>
          </div>
        </Box>
      </Modal>

    </div>
  )
}

export { ChatComponent }
